import React, { useEffect, useRef, useState } from 'react'
import { IconButton, InputAdornment, TextField, Typography, useMediaQuery } from '@material-ui/core'
import { DeleteOutline as DeleteIcon } from '@material-ui/icons'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import { StyledSwitch } from 'shared'
import {
  LAP_PERCENTAGE_REQUIRED_MAX,
  LAP_PERCENTAGE_REQUIRED_MIN,
  STRING_EMPTY
} from 'utils/constants'

import { SectionBlock } from '../SectionBlock'

import { useStyles } from './PointsSection.style'

const PointsSection = () => {
  const classes = useStyles()
  const {
    values,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setValues,
    handleChange,
    initialValues
  } = useFormikContext()

  const firstRender = useRef(false)

  const [lastAddedPosition, setLastAddedPosition] = useState(null)
  const [showPoints, setShowPoints] = useState(!!values.championshipPoints.length)
  const [pointFields, setPointFields] = useState([...values.championshipPoints])
  const [showAssistancePoint, setShowAssistancePoint] = useState(initialValues.assistancePoint > 0)
  const [showAttendanceFields, setShowAttendanceFields] = useState(() =>
    values.fixtures.reduce(
      (acc, fixture) => ({
        ...acc,
        [fixture.dateNumber]: fixture.points.some((point) => point.position === 0)
      }),
      {}
    )
  )
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('xs'))

  useEffect(() => {
    firstRender.current = true
  }, [])

  const handleAddPointField = () => {
    setPointFields((prevFields) => [
      ...prevFields,
      {
        position: STRING_EMPTY,
        points: STRING_EMPTY,
        name: STRING_EMPTY
      }
    ])

    const index = pointFields.length - 1

    setFieldValue(`championshipPoints[${index + 1}]`, {
      points: 1,
      position: STRING_EMPTY,
      name: STRING_EMPTY
    })
  }

  const removePointField = (index, setValues) => {
    setPointFields((prevFields) => prevFields.filter((_, i) => i !== index))

    setValues((values) => {
      const championshipPoints = values.championshipPoints.filter((_, i) => i !== index)
      setShowPoints(championshipPoints.length)

      return {
        ...values,
        championshipPoints
      }
    })
  }

  const normalizeFixturePositionPoints = (fixturePoints = []) => {
    const attendancePoints = fixturePoints.filter((point) => point.position === 0)
    const positionPoints = fixturePoints
      .filter((point) => point.position !== 0)
      .sort((a, b) => a.position - b.position)
      .map((point, index) => ({
        ...point,
        position: index + 1
      }))

    return [...attendancePoints, ...positionPoints]
  }

  const updateFixturePositionValue = (fixtureIndex, position, value) => {
    const currentFixturePoints = values.fixtures[fixtureIndex]?.points ?? []
    const remainingPoints = [
      ...currentFixturePoints.filter((point) => point.position === 0),
      ...currentFixturePoints.filter((point) => point.position !== 0 && point.position !== position)
    ]
    setFieldValue(`fixtures[${fixtureIndex}].points`, [
      ...remainingPoints,
      {
        points: value === STRING_EMPTY ? STRING_EMPTY : Number(value),
        position
      }
    ])
  }

  const addFixturePositionField = (fixtureIndex) => {
    const currentFixturePoints = values.fixtures[fixtureIndex]?.points ?? []
    const normalizedPoints = normalizeFixturePositionPoints(currentFixturePoints)
    const nextPosition = normalizedPoints.filter((point) => point.position !== 0).length + 1

    setLastAddedPosition(nextPosition)

    setFieldValue(`fixtures[${fixtureIndex}].points`, [
      ...normalizedPoints,
      {
        points: 1,
        position: nextPosition
      }
    ])
  }

  const removeFixturePositionField = (fixtureIndex, position) => {
    const currentFixturePoints = values.fixtures[fixtureIndex]?.points ?? []
    const nextPoints = currentFixturePoints.filter((point) => point.position !== position)

    setFieldValue(`fixtures[${fixtureIndex}].points`, normalizeFixturePositionPoints(nextPoints))
  }

  const updateFixtureAttendanceValue = (fixtureIndex, value) => {
    const currentFixturePoints = values.fixtures[fixtureIndex]?.points ?? []
    const nextPoints = currentFixturePoints.filter((point) => point.position !== 0)

    setFieldValue(
      `fixtures[${fixtureIndex}].points`,
      value === STRING_EMPTY ? nextPoints : [...nextPoints, { points: Number(value), position: 0 }]
    )
  }

  const renderAssistancePoints = () =>
    values.useFixtureAttendancePoints ? (
      <div>
        {values.fixtures.map((fixture, index) => {
          const attendancePoint =
            fixture.points.find((p) => p.position === 0)?.points ?? STRING_EMPTY
          const hasAttendancePoint = fixture.points.some((point) => point.position === 0)
          const showField = hasAttendancePoint || showAttendanceFields[fixture.dateNumber]

          return (
            <div key={fixture.dateNumber} className={classes.assistancePointContainer}>
              <Typography color='primary' variant='h6' className={classes.assistancePointTitle}>
                Fecha {fixture.dateNumber}
              </Typography>
              {showField ? (
                <TextField
                  className={classes.input}
                  name={`fixtures[${index}].points`}
                  autoComplete='off'
                  value={attendancePoint}
                  type='number'
                  inputProps={{
                    step: 1,
                    min: 0
                  }}
                  onChange={(e) => updateFixtureAttendanceValue(index, e.target.value)}
                  onBlur={handleBlur}
                  helperText={errors.fixtures && errors.fixtures[index]?.points}
                  error={
                    touched.fixtures && errors.fixtures && Boolean(errors.fixtures[index]?.points)
                  }
                  variant='outlined'
                  size='small'
                  margin='none'
                />
              ) : (
                <Typography
                  color='primary'
                  variant='h6'
                  className={classes.addLink}
                  onClick={() => {
                    updateFixtureAttendanceValue(index, 0)
                    setShowAttendanceFields((prev) => ({
                      ...prev,
                      [fixture.dateNumber]: true
                    }))
                  }}>
                  + Añadir presentismo
                </Typography>
              )}
            </div>
          )
        })}
      </div>
    ) : !showAssistancePoint ? (
      <Typography
        color='primary'
        variant='h6'
        className={classes.addLink}
        onClick={() => setShowAssistancePoint(true)}>
        + Añadir puntaje
      </Typography>
    ) : (
      <TextField
        className={classes.input}
        name='assistancePoint'
        autoComplete='off'
        value={values.assistancePoint}
        onChange={handleChange}
        onBlur={handleBlur}
        helperText={errors.assistancePoint}
        error={touched.assistancePoint && Boolean(errors.assistancePoint)}
        type='number'
        inputProps={{
          step: 1,
          min: 0
        }}
        variant='outlined'
        size='small'
        margin='none'
      />
    )

  const renderPositionPoints = () =>
    values.useFixturePositionPoints ? (
      <div>
        {values.fixtures.map((fixture, fixtureIndex) => {
          const fixturePositionPoints = (fixture.points || [])
            .filter((point) => point.position !== 0)
            .sort((a, b) => a.position - b.position)

          return (
            <div key={fixture.dateNumber} className={classes.assistancePointContainer}>
              <Typography color='primary' variant='h6' className={classes.assistancePointTitle}>
                Fecha {fixture.dateNumber}
              </Typography>
              <div className={classes.content}>
                <div className={classes.block}>
                  <Typography color='primary' variant='h6' className={classes.position}>
                    Puesto
                  </Typography>
                  <Typography color='primary' variant='h6' className={classes.points}>
                    Puntos
                  </Typography>
                </div>

                {fixturePositionPoints.map((point) => (
                  <div key={`${fixture.dateNumber}-${point.position}`} className={classes.block}>
                    <TextField
                      className={clsx(classes.input, classes.position)}
                      autoComplete='off'
                      fullWidth
                      value={point.position}
                      onBlur={handleBlur}
                      required
                      type='number'
                      disabled
                      inputProps={{
                        step: 1,
                        min: 1
                      }}
                      variant='outlined'
                      size='small'
                      margin='none'
                    />
                    <TextField
                      className={clsx(classes.input, classes.points)}
                      autoComplete='off'
                      fullWidth={isDesktop}
                      type='number'
                      inputProps={{
                        step: 1,
                        min: 1
                      }}
                      value={point.points ?? STRING_EMPTY}
                      onChange={(e) =>
                        updateFixturePositionValue(fixtureIndex, point.position, e.target.value)
                      }
                      onBlur={handleBlur}
                      variant='outlined'
                      size='small'
                      margin='none'
                      autoFocus={point.position === lastAddedPosition}
                    />
                    <IconButton
                      color='primary'
                      size='small'
                      className={classes.deleteButton}
                      onClick={() => removeFixturePositionField(fixtureIndex, point.position)}>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))}
              </div>

              <div className={classes.content}>
                <Typography
                  color='primary'
                  variant='h6'
                  className={classes.addLink}
                  onClick={() => addFixturePositionField(fixtureIndex)}>
                  + Añadir puesto
                </Typography>
              </div>
            </div>
          )
        })}
      </div>
    ) : !showPoints ? (
      <Typography
        className={classes.addLink}
        color='primary'
        variant='h6'
        onClick={() => {
          setShowPoints(true)
          handleAddPointField()
        }}>
        + Añadir puntaje
      </Typography>
    ) : (
      <>
        <div className={classes.content}>
          <div className={classes.block}>
            <Typography color='primary' variant='h6' className={classes.position}>
              Puesto
            </Typography>
            <Typography color='primary' variant='h6' className={classes.points}>
              Puntos
            </Typography>
          </div>

          {pointFields.map((_, index) => (
            <div key={index} className={classes.block}>
              <TextField
                className={clsx(classes.input, classes.position)}
                name='position'
                autoComplete='off'
                fullWidth
                value={index + 1}
                onBlur={handleBlur}
                required
                type='number'
                disabled
                inputProps={{
                  step: 1,
                  min: 1
                }}
                variant='outlined'
                size='small'
                margin='none'
              />
              <TextField
                className={clsx(classes.input, classes.points)}
                name={`championshipPoints[${index}].points`}
                autoComplete='off'
                fullWidth={isDesktop}
                type='number'
                inputProps={{
                  step: 1,
                  min: 1
                }}
                value={values.championshipPoints[index]?.points ?? STRING_EMPTY}
                onChange={(e) =>
                  setFieldValue(`championshipPoints[${index}].points`, e.target.value)
                }
                onBlur={handleBlur}
                helperText={
                  Array.isArray(errors.championshipPoints)
                    ? errors.championshipPoints[index]?.points
                    : STRING_EMPTY
                }
                error={
                  touched.championshipPoints &&
                  Array.isArray(errors.championshipPoints) &&
                  Boolean(errors.championshipPoints[index]?.points)
                }
                required
                variant='outlined'
                size='small'
                autoFocus={firstRender.current}
                margin='none'
              />

              <IconButton
                color='primary'
                size='small'
                className={classes.deleteButton}
                onClick={() => removePointField(index, setValues)}>
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>
        <div className={classes.content}>
          <Typography
            color='primary'
            variant='h6'
            className={classes.addLink}
            onClick={handleAddPointField}>
            + Añadir puntaje
          </Typography>
        </div>
      </>
    )

  return (
    <SectionBlock title='Puntaje'>
      <div className={classes.optionController}>
        <Typography color='primary' variant='h6'>
          Puntaje variable por fecha
        </Typography>
        <div className={classes.switchContainer}>
          <Typography variant='h6' className={classes.switchTitle}>
            No
          </Typography>
          <StyledSwitch
            checked={values.useFixturePositionPoints}
            onChange={handleChange}
            onBlur={handleBlur}
            name='useFixturePositionPoints'
          />
          <Typography variant='h6' className={classes.switchTitle}>
            Si
          </Typography>
        </div>
      </div>
      <div className={classes.optionController}>
        <Typography color='primary' variant='h6'>
          Presentismo variable por fecha
        </Typography>
        <div className={classes.switchContainer}>
          <Typography variant='h6' className={classes.switchTitle}>
            No
          </Typography>
          <StyledSwitch
            checked={values.useFixtureAttendancePoints}
            onChange={handleChange}
            onBlur={handleBlur}
            name='useFixtureAttendancePoints'
          />
          <Typography variant='h6' className={classes.switchTitle}>
            Si
          </Typography>
        </div>
      </div>
      <Typography className={classes.assistancePointTitle} color='primary' variant='h6'>
        Puntos por puesto
      </Typography>
      <div className={clsx(classes.block, 'alt')}>
        <div className={clsx(classes.otherContainer, classes.altOtherContainer)}>
          <Typography variant='h6' color='primary' className={classes.numberLabel}>
            Defina el % de vueltas que deben completar para puntuar
          </Typography>
          <TextField
            name='minLapPercentageRequired'
            value={values.minLapPercentageRequired}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='-'
            error={touched.minLapPercentageRequired && Boolean(errors.minLapPercentageRequired)}
            autoComplete='off'
            variant='outlined'
            size='small'
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>
            }}
            inputProps={{
              step: 1,
              min: LAP_PERCENTAGE_REQUIRED_MIN,
              max: LAP_PERCENTAGE_REQUIRED_MAX
            }}
            type='number'
            className={clsx(classes.input, 'alt')}
            margin='none'
          />
        </div>

        {errors.minLapPercentageRequired && (
          <Typography
            color={touched.minLapPercentageRequired ? 'error' : 'inherit'}
            className={classes.altErrorMessage}>
            {errors.minLapPercentageRequired}
          </Typography>
        )}
      </div>

      {renderPositionPoints()}

      <div className={clsx(classes.block, 'alt')}>
        <Typography color='primary' variant='h6' className={classes.assistancePointTitle}>
          Puntos por presentismo
        </Typography>

        {renderAssistancePoints()}
      </div>
    </SectionBlock>
  )
}

export default PointsSection
